import {useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';

import {SEARCH_TYPES_BY_ID} from '@embertracking/common/data/search/types.mjs';
import {hasFeature} from '-/features';
import {MIN_QUERY_LENGTH} from './constants';
import {callFunction} from '-/data/utils';
import {reportError} from '-/data/errorReporting';
import useLocalResults from './useLocalResults';
import useMemberTabResults, {splitMemberTabQuery} from './useMemberTabResults';
import {orderGroups} from './groupOrder';

// Debounced, abortable global search: POST /search for the record types, plus
// the locally-matched catalogs (reports, settings, nav) merged in.
//
// 250 ms is the ceiling on how long a keystroke can sit unanswered; the abort is
// what stops the answer to "chel" arriving after the answer to "chelsea" and
// overwriting it. Both matter more here than on a normal grid filter, because
// the dropdown re-ranks on every character.

const DEBOUNCE_MS = 250;

// Answers are kept for the lifetime of the mount, keyed by the exact request.
// Closing the field and opening it again must NOT re-run the search you just
// ran — the field still holds the query, the results are still on screen, and
// re-fetching them makes a dismissed-by-accident dropdown feel like it lost its
// place. The index behind /search has its own 5-minute TTL, so serving the last
// answer here costs nothing in freshness.
const CACHE_LIMIT = 24;

// How many members a "name + tab" query considers. Someone typing "brooks
// service" means one person; a handful covers shared surnames without turning
// the dropdown into a list of tabs.
const TAB_MEMBER_LIMIT = 5;

const EMPTY = {loading: false, groups: [], total: 0, truncated: {}, tabMembers: [], error: null};

export default function useSearchQuery(query, {types, limit, includeArchived = false, enabled = true} = {}) {
    const [state, setState] = useState(EMPTY);
    const controllerRef = useRef(null);
    const cacheRef = useRef(new Map());

    // Matched in the browser against catalogs the app already holds, so these
    // render on the first keystroke rather than waiting on the request below.
    const localGroups = useLocalResults(query, {limit});
    const expandMemberTabs = useMemberTabResults();

    // "brooks service" -> {nameQuery: 'brooks', sectionTokens: ['service']}, or
    // null when the query isn't a name-plus-tab. See useMemberTabResults.
    const tabSplit = useMemo(() => splitMemberTabQuery(query), [query]);
    const tabKey = tabSplit ? tabSplit.nameQuery : '';

    // `types` is almost always a fresh array literal from the caller; compare by
    // value so passing ['training'] inline doesn't re-fire the effect forever.
    const typesKey = types ? [...types].sort().join(',') : '';

    useEffect(() => {
        controllerRef.current?.abort();
        controllerRef.current = null;

        const trimmed = (query || '').trim();

        // Closed (or re-opened) is not a reason to throw the answer away — the
        // state is simply left as it was, so re-opening shows what was there.
        if (!enabled) return undefined;

        if (trimmed.length < MIN_QUERY_LENGTH) {
            setState(EMPTY);
            return undefined;
        }

        const cacheKey = [trimmed, typesKey, limit, includeArchived, tabKey].join(' ');
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setState({...cached, loading: false, error: null});
            return undefined;
        }

        // Deliberately does NOT clear `groups`: narrowing to a type re-requests
        // with a bigger limit, and blanking the page to skeletons for that round
        // trip reads as a full reload of something the user was already looking
        // at. The previous rows stay put until the new ones land.
        setState(previous => ({...previous, loading: true, error: null}));

        const timer = setTimeout(async () => {
            const controller = new AbortController();
            controllerRef.current = controller;

            // No leading slash: resolveApiBase() already returns a base ending
            // in "/", so "/search" would post to a doubled slash — which Express
            // does not match, and the api's log middleware skips 404s, so the
            // request would fail completely silently.
            const ask = payload => callFunction('search', payload, {signal: controller.signal});

            try {
                const [data, tabData] = await Promise.all([
                    ask({
                        query: trimmed,
                        ...(typesKey ? {types: typesKey.split(',')} : {}),
                        ...(limit ? {limit} : {}),
                        includeArchived
                    }),
                    // The member half of a "name + tab" query. Fired only when a
                    // section word is present, because the full query can never
                    // match the member (token-AND, and "service" isn't anywhere
                    // on their record) — that is the whole reason it exists.
                    tabSplit ? ask({query: tabSplit.nameQuery, types: ['members'], limit: TAB_MEMBER_LIMIT}) : null
                ]);

                if (controller.signal.aborted) return;
                const next = {
                    groups: data?.groups || [],
                    total: data?.total || 0,
                    truncated: data?.truncated || {},
                    tabMembers: tabData?.groups?.find(group => group.type === 'members')?.items || []
                };

                const cache = cacheRef.current;
                cache.set(cacheKey, next);
                // Plain FIFO eviction — Map preserves insertion order, and the
                // oldest query in a typing session is the least likely to be
                // asked for again.
                if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);

                setState({...next, loading: false, error: null});
            } catch (e) {
                // A superseded keystroke is the expected path, not a fault.
                if (axios.isCancel(e) || e?.name === 'CanceledError' || controller.signal.aborted) return;
                reportError(e, {context: 'useSearchQuery', component: 'GlobalSearch'});
                setState({...EMPTY, error: e});
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controllerRef.current?.abort();
        };
        // `tabSplit` is derived from `query` alone, so `tabKey` already covers it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, typesKey, limit, includeArchived, enabled, tabKey]);

    useEffect(() => () => controllerRef.current?.abort(), []);

    const groups = useMemo(() => {
        const scoped = typesKey ? new Set(typesKey.split(',')) : null;
        const wanted = group => !scoped || scoped.has(group.type);

        const tabs = expandMemberTabs(tabSplit, state.tabMembers);

        let merged = state.groups;
        if (tabs.length && (!scoped || scoped.has('members'))) {
            const existing = merged.find(group => group.type === 'members');
            const items = [...tabs, ...(existing?.items || [])].sort((a, b) => b.score - a.score);
            const group = {
                type: 'members',
                label: SEARCH_TYPES_BY_ID.members.label,
                items,
                count: items.length,
                total: (existing?.total || 0) + tabs.length
            };
            merged = existing
                ? merged.map(entry => (entry.type === 'members' ? group : entry))
                : [...merged, group];
        }

        // The api filters by PERMISSION; feature flags live on the client
        // (src/features.js reads SettingsContext), so a department that doesn't
        // run Preplans or Fuel drops those groups here rather than the api
        // needing its own copy of the flag logic.
        const enabledType = type => {
            const {feature} = SEARCH_TYPES_BY_ID[type] || {};
            return !feature || hasFeature(feature);
        };

        return orderGroups([...merged, ...localGroups.filter(wanted)].filter(group => enabledType(group.type)));
    }, [state.groups, state.tabMembers, localGroups, typesKey, tabSplit, expandMemberTabs]);

    const total = useMemo(
        () => groups.reduce((sum, group) => sum + group.total, 0),
        [groups]
    );

    return {...state, groups, total};
}
